// Plate-loading calculator. Pure function — given a target total
// weight, a bar weight, and an inventory of plate denominations,
// return the per-side plate stack that approximates the target. Used
// by the workout logger's "show plates" affordance so the athlete
// reads the load-out without doing the arithmetic on the gym floor.
//
// Algorithm: greedy by descending plate weight, capped by available
// pairs (each plate is loaded as a pair, one per side, so the
// inventory is per-pair). Float arithmetic uses round-to-3-decimals
// at every step so the typical 2.5 + 1.25 + 0.625 fractional kg case
// doesn't accumulate IEEE drift.
//
// Half-bar mode (used for landmine OHP / one-handed work): when only
// one side of the bar is loaded, the formula becomes
// target = bar + halfBarOffset + sideLoad, where halfBarOffset is the
// bar's CG shift produced by lifting from one end. We don't model
// halfBarOffset yet — the client passes a separate `barWeight` for
// half-bar lifts (typical: half the regular bar's weight) and the
// calc proceeds the same. Half-bar is a profile-level UX flag, not
// a calculator-level one.
//
// Inventory shape: `{ size: number; pairs: number }[]` plus the unit it is
// stamped in (`PlateInventory`). Each entry is
// one plate denomination plus how many pairs the gym has. The
// default JL inventory matches a typical home / commercial
// setup with metric plates; the editor can override per profile in
// a future phase.

import { roundTo } from "./units.js";

export type PlateInventoryEntry = {
  /** The plate's face value **in the inventory's own unit** — 25 means a 25 kg
   *  plate in a metric gym and a 25 lb plate in a pound one.
   *
   *  ⛔ It is NOT called `kg`, and that is the whole point of LG-37b E1: a
   *  field that holds kilos on one athlete's phone and pounds on another's
   *  while calling itself `kg` is the same defect this repo already booked
   *  twice — `CN-30` (`TargetItem.value`) and `PG-14` (`painMap`). The unit
   *  lives beside the list, in `PlateInventory.unit`, never inside a name. */
  size: number;
  pairs: number;
};

export type PlatePairLoaded = {
  /** Face value in the calculation's unit — see `PlateInventoryEntry.size`. */
  size: number;
  pairs: number;
};

/** An inventory is a list of denominations **plus the unit they are stamped
 *  in**. A pound gym racks 45/35/25/10/5/2.5 lb plates; those are not kilos
 *  converted, they are what is written on the iron (`equipment-model.md`
 *  «L'invariante che sopravvive a tutto»: resolve in the equipment's real unit, convert only to show). */
export type PlateInventory = {
  unit: "kg" | "lb";
  plates: PlateInventoryEntry[];
};

// How the plates sit on the bar, as the CALCULATOR names it. Distinct from
// `barLoading` ('standard' | 'single-side'), which is how the CATALOG and the
// athlete's stored preference name the same two shapes — one word for the
// question, one for the answer, and `computePlates` is the translator.
export type PlateLoadMode = "pairs" | "single";

/** The shape the ROUNDING family reads: a plate shape, or `'handheld'` for
 *  iron with no plates at all (a fixed dumbbell). ⛔ Not part of
 *  `PlateLoadMode` on purpose: that type also names what the plate calculator
 *  ANSWERS (`PlateCalculation.mode`), and a dumbbell is never plate-loaded. */
export type LoadableMode = PlateLoadMode | "handheld";

export type PlateCalculation =
  | {
      ok: true;
      // 'pairs' = standard symmetric loading on both bar ends (default).
      // 'single' = landmine / one-side loading: the plates load on a
      // single end, so `perSide` reads as the loaded-end total and
      // each `pairsLoaded.pairs` is a plate count (not a pair count).
      mode: PlateLoadMode;
      perSide: number;
      pairsLoaded: PlatePairLoaded[];
      // Tail = what the inventory could not represent (pairs: total across
      // both sides). 0 = exact; positive = inexact, whether the target is too
      // heavy or falls between denominations. We still return the closest
      // load from below so the lift isn't aborted.
      remainder: number;
    }
  | { ok: false; reason: "below_bar" | "invalid_input" };

// Local name kept on purpose (see units.ts's roundWeight doc): this is gram
// precision used DEFENSIVELY inside the packing algorithm, not a loadable
// grid, so it is not `roundWeight`. The arithmetic itself is shared, though.
const round3 = (n: number): number => roundTo(n, 3);

// JL default plate inventory — the seven denominations a commercial metric
// gym actually racks, 1.25 → 25 kg, with a generous pair count. The greedy
// walk only consumes what it needs, so pair counts are a "max available" hint
// not a target.
//
// ⛔ No 0.5 kg. It was in here and Antenore took it out (10/09): "nelle
// palestre commerciali in kg non ho mai visto mezzo kg". Half-kilo discs are
// change plates — competition platforms and micro-loading — not gym furniture,
// and every one of them cost a slot in the tap-to-add row.
//
// ⚠️ Two consequences, both real. An athlete who already SAVED an inventory
// keeps the 0.5 they were given: this constant seeds, it does not migrate, and
// rewriting their stored choice would be worse. And with no 0.5 the default
// `smallestLoadableStepKg` goes from 1 kg to 2.5 kg, so a %1RM prescription
// floors to a coarser rung for anyone who never configured plates — which is
// the honest number for that gym, not a regression.
export const DEFAULT_PLATE_INVENTORY: PlateInventoryEntry[] = [
  { size: 25, pairs: 4 },
  { size: 20, pairs: 4 },
  { size: 15, pairs: 2 },
  { size: 10, pairs: 4 },
  { size: 5, pairs: 4 },
  { size: 2.5, pairs: 4 },
  { size: 1.25, pairs: 4 },
];

// The same rack, in a gym that racks POUNDS: 45/35/25/10/5/2.5 lb, which is
// what is written on that iron — ⛔ NOT the metric list run through 2.2046.
// `equipment-model.md` «L'invariante che sopravvive a tutto» is the invariant: resolve in the equipment's
// real unit, convert only to show. A "kg inventory converted at runtime" would
// offer the athlete a 11.34 lb plate, which no gym owns.
//
// Pair counts mirror the metric list's shape — generous, with the odd
// denomination (35 lb, as 15 kg) scarcer, because the greedy only consumes
// what it needs and a pair count is a "max available" hint.
//
// ⚠️ The smallest is 2.5 lb, so the un-configured pairs step is 5 lb — which is
// exactly `DEFAULT_LOADABLE_STEP_LB`, and that agreement is not a coincidence
// to be broken quietly: change one and `defaultLoadableStepFor` starts lying.
export const DEFAULT_PLATE_INVENTORY_LB: PlateInventoryEntry[] = [
  { size: 45, pairs: 4 },
  { size: 35, pairs: 2 },
  { size: 25, pairs: 4 },
  { size: 10, pairs: 4 },
  { size: 5, pairs: 4 },
  { size: 2.5, pairs: 4 },
];

// Standard Olympic barbell weight defaults. Used when the profile
// hasn't configured a per-exercise barbell weight. The 7 kg curl-bar
// and 11 kg trap-bar live in the per-exercise barbell map — added
// as a future phase.
export const DEFAULT_BARBELL_KG = 20;

/** The pound gym's standard Olympic bar. 45 lb is the bar that is SOLD as a
 *  45, not 20 kg re-labelled (20 kg is 44.09 lb) - same «L'invariante che sopravvive a tutto» rule. */
export const DEFAULT_BARBELL_LB = 45;

// Default loadable increments used when the athlete hasn't configured a
// plate inventory (LG-15). Kg default mirrors StrengthLog's "nearest 2.5 kg";
// lb default is 5 lb (a pair of 2.5 lb plates) — the common finest step in a
// pound gym. These are the fallback for `roundLoadable`; a configured
// inventory refines the kg step via `smallestLoadableStepKg`. Kept here beside
// the inventory constant so "how big is one plate step" has one home.
export const DEFAULT_LOADABLE_STEP_KG = 2.5;
export const DEFAULT_LOADABLE_STEP_LB = 5;

// ── "Which default, for the unit I'm holding?" (LG-37b E1) ─────────────────
//
// Three one-liners and no cleverness, on purpose. Every one of them used to be
// a `unit === 'lb' ? X : Y` written at the call site, and the call sites are
// four (bar seed, inventory seed, rounding step, storage fallback) across two
// platform twins — which is exactly how one of them ends up picking the kg
// branch for a pound athlete and nobody notices.
//
// ⛔ None of these converts. `defaultBarFor('lb')` is 45 because that is the
// bar, not because 20 kg rounds there.

export const defaultInventoryFor = (unit: "kg" | "lb"): PlateInventoryEntry[] =>
  unit === "lb" ? DEFAULT_PLATE_INVENTORY_LB : DEFAULT_PLATE_INVENTORY;

export const defaultBarFor = (unit: "kg" | "lb"): number =>
  unit === "lb" ? DEFAULT_BARBELL_LB : DEFAULT_BARBELL_KG;

/** The step for an athlete who has NEVER configured plates. ⚠️ Deliberately
 *  the constant and not `smallestLoadableStep(defaultInventoryFor(unit))`:
 *  Antenore's decision of 2026-07-20 (Option 1) is that the un-configured
 *  athlete floors to the conservative default, and only a real stored
 *  inventory refines it. The two happen to agree today (2×1.25 = 2.5,
 *  2×2.5 = 5) — deriving would make that agreement load-bearing. */
export const defaultLoadableStepFor = (unit: "kg" | "lb"): number =>
  unit === "lb" ? DEFAULT_LOADABLE_STEP_LB : DEFAULT_LOADABLE_STEP_KG;

/** Fixed dumbbells go up by 1 or 2 (kg or lb, per the athlete's rack). The
 *  un-configured default is 2, never 2.5: a step of 2 is loadable on a rack
 *  that goes by 1, while 2.5 is not on either (Antenore, 2026-11-03). Same
 *  number in both units, and it does not convert. */
export const DEFAULT_HANDHELD_STEP = 2;

/** The steps an athlete can pick for their dumbbell rack, per unit. */
export const HANDHELD_STEP_OPTIONS: Record<"kg" | "lb", ReadonlyArray<number>> = {
  kg: [1, 2, 2.5, 5],
  lb: [1, 2, 5],
};

// Finest total-load increment an inventory can express. Symmetric ("pairs")
// loading adds a plate to BOTH ends at once, so the smallest step is 2× the
// smallest plate; single-side ("single", landmine) adds one plate → 1×. Used
// by the %1RM rounding path so a computed target snaps to a weight the athlete
// can actually build (LG-15).
//
// ⚠️ `fallback` is a parameter and not a kg constant because the answer is in
// the INVENTORY's unit: a pound rack's finest step is 5 lb, not 2.5 kg. The
// caller knows which unit it is holding; this function must not guess
// (LG-37b E1).
export const smallestLoadableStep = (
  inventory: PlateInventoryEntry[] = DEFAULT_PLATE_INVENTORY,
  mode: PlateLoadMode = "pairs",
  fallback: number = DEFAULT_LOADABLE_STEP_KG,
): number => {
  const usable = inventory.filter((p) => p.size > 0 && p.pairs > 0);
  if (usable.length === 0) return fallback;
  return plateStep(Math.min(...usable.map((p) => p.size)), mode);
};

// What ONE plate of `size` moves the total by, under a given loading mode.
// Symmetric loading puts a plate on both ends at once — one "plate" is a pair,
// so the bar moves by 2×; single-side loading puts one plate on one end and
// moves it by 1×.
//
// Extracted for LG-37b fetta B, where the athlete composes the load by tapping
// plates: that block needs the same ×2/×1 rule `smallestLoadableStep` needed,
// and writing it a second time by hand is how the two drift apart the day the
// rule gains a case (half-bar offset, micro-plates loaded singly).
export const plateStep = (size: number, mode: PlateLoadMode): number =>
  round3(mode === "pairs" ? size * 2 : size);

/** The catalog's word for the loading shape, translated into the calculator's.
 *
 *  ⛔ CN-39 — it exists because the translation was written inline in three
 *  places and one of them, `roundingStepFor`, did not write it at all: it
 *  hardcoded `'pairs'`, so the seed and the «≈» button proposed two different
 *  ladders for the same iron. A one-line mapping copied by hand is a one-line
 *  mapping somebody forgets. */
/** A dumbbell: held, not racked. The one place that says which equipment is
 *  handheld, so the call sites ask instead of comparing strings. */
export const isHandheld = (equipment: string | undefined): boolean =>
  equipment === "dumbbell";

export function plateLoadModeFor(
  barLoading?: "standard" | "single-side",
): PlateLoadMode;
/** With `equipment` the answer can also be `'handheld'` (a dumbbell), which
 *  wins over the loading shape: nothing is racked on a dumbbell. */
export function plateLoadModeFor(
  barLoading: "standard" | "single-side" | undefined,
  equipment: string | undefined,
): LoadableMode;
export function plateLoadModeFor(
  barLoading?: "standard" | "single-side",
  equipment?: string,
): LoadableMode {
  if (isHandheld(equipment)) return "handheld";
  return barLoading === "single-side" ? "single" : "pairs";
}

export const computePlates = (params: {
  target: number;
  bar?: number;
  inventory?: PlateInventoryEntry[];
  /** Loading shape. 'standard' (default) loads pairs symmetrically;
   *  'single-side' loads a single stack on one side only (landmine,
   *  T-bar, sled, per-arm lever). */
  barLoading?: "standard" | "single-side";
}): PlateCalculation => {
  const bar = params.bar ?? DEFAULT_BARBELL_KG;
  const target = params.target;
  const mode: PlateLoadMode = plateLoadModeFor(params.barLoading);
  if (!Number.isFinite(target) || target < 0 || !Number.isFinite(bar) || bar < 0) {
    return { ok: false, reason: "invalid_input" };
  }
  if (target < bar) {
    return { ok: false, reason: "below_bar" };
  }
  const totalLoad = round3(target - bar);
  const inv = (params.inventory ?? DEFAULT_PLATE_INVENTORY)
    .filter((p) => p.size > 0 && p.pairs > 0)
    .slice()
    .sort((a, b) => b.size - a.size);

  if (mode === "single") {
    // Landmine: a single plate stack on the loaded end. Each inventory
    // entry's `pairs` count contributes 2 plates (one pair = 2 plates),
    // so the available count per kg is `pairs × 2` for landmine work.
    let remaining = totalLoad;
    const platesLoaded: PlatePairLoaded[] = [];
    for (const entry of inv) {
      if (remaining <= 0) break;
      // ⚠️ DEFENSIVE AND CURRENTLY DEAD — the `+ 1e-6` cannot change this
      // result, and the same is true of its twin in the pairs loop below.
      // It is there to stop a quotient that lands a hair under an integer
      // (49.999999999999996) from flooring to 48 — but `round3` already
      // snapped the quotient to 0.001 BEFORE the epsilon is added, and
      // realistic IEEE-754 drift (~1e-16 relative) is orders of magnitude
      // below that snap radius. Verified by exhaustive sweep (kg
      // 0.001-3.000 × k 1-5000) and a 5M-round fuzz of the full loop with
      // and without it: zero divergences.
      //
      // Kept rather than deleted because it becomes load-bearing again the
      // moment anyone removes the `round3` around the division — which is
      // exactly the kind of "simplification" that looks safe. Concretely,
      // with 0.05 kg microplates and 0.15 kg remaining, the raw quotient is
      // 2.9999999999999996: without round3 the floor gives 2 plates instead
      // of 3, and only the epsilon saves it. Across 0.05-step plate weights
      // that case is common, not exotic. If you take round3 out, keep the
      // epsilon and add a test.
      const maxByRemainder = Math.floor(round3(remaining / entry.size) + 1e-6);
      const platesAvailable = entry.pairs * 2;
      const platesToUse = Math.min(maxByRemainder, platesAvailable);
      if (platesToUse <= 0) continue;
      // `pairs` field reused as plate count for single-stack loading.
      platesLoaded.push({ size: entry.size, pairs: platesToUse });
      remaining = round3(remaining - platesToUse * entry.size);
    }
    return {
      ok: true,
      mode: "single",
      perSide: round3(totalLoad - remaining),
      pairsLoaded: platesLoaded,
      remainder: round3(remaining),
    };
  }

  // Standard pairs loading. Each pair covers one plate per side, so
  // load-per-side decreases by entry.size per pair.
  const perSide = round3(totalLoad / 2);
  let perSideRemaining = perSide;
  const pairsLoaded: PlatePairLoaded[] = [];
  for (const entry of inv) {
    if (perSideRemaining <= 0) break;
    // Same defensive-and-dead epsilon as the single-stack loop above; the
    // reasoning and the "if you remove round3, keep this" note live there.
    const maxByRemainder = Math.floor(round3(perSideRemaining / entry.size) + 1e-6);
    const pairsToUse = Math.min(maxByRemainder, entry.pairs);
    if (pairsToUse <= 0) continue;
    pairsLoaded.push({ size: entry.size, pairs: pairsToUse });
    perSideRemaining = round3(perSideRemaining - pairsToUse * entry.size);
  }
  return {
    ok: true,
    mode: "pairs",
    perSide: round3(perSide - perSideRemaining),
    pairsLoaded,
    remainder: round3(perSideRemaining * 2),
  };
};

// ---------------------------------------------------------------------------
// Composing (LG-37b fetta B) — the OTHER direction.
//
// `computePlates` answers "I want 100 kg, what do I load?". These three answer
// "the bar in front of me has these plates on it, what does that weigh?" —
// which is the question you actually have when the bar is ALREADY loaded, and
// the one the athlete asked for by tapping plates instead of typing a number.
//
// They live here, beside the greedy, because they share its arithmetic: the
// same ×2/×1 rule, the same round-at-every-step discipline, and the same
// output shape. A composition rendered through `compositionToCalculation`
// reaches the UI as a `PlateCalculation`, so the summary that reads it does
// not need to know which of the two directions produced it.
// ---------------------------------------------------------------------------

/** Plates the athlete has put on the bar by hand: size → how many (pairs in
 *  'pairs' mode, single plates in 'single'), same field reuse as
 *  `PlateCalculation.pairsLoaded`. */
export type PlateComposition = ReadonlyArray<PlatePairLoaded>;

/** What a hand-built composition weighs in total, bar included. */
export const composedTotal = (
  composition: PlateComposition,
  bar: number,
  mode: PlateLoadMode,
): number =>
  round3(
    composition.reduce(
      (sum, p) => round3(sum + plateStep(p.size, mode) * p.pairs),
      bar,
    ),
  );

/**
 * Present a hand-built composition in the shape `computePlates` returns, so
 * the summary renders it through the same branch.
 *
 * ⛔ `remainder` is always 0 and that is not a shortcut: the remainder means
 * "the inventory could not match your target", and a composition has no target
 * to miss — it IS the load. A composition that cannot be built is prevented at
 * the tap (`loadablePlateCount`), not reported afterwards.
 */
export const compositionToCalculation = (
  composition: PlateComposition,
  bar: number,
  mode: PlateLoadMode,
): PlateCalculation => {
  if (!Number.isFinite(bar) || bar < 0) {
    return { ok: false, reason: "invalid_input" };
  }
  const loaded = composition
    .filter((p) => p.size > 0 && p.pairs > 0)
    .map((p) => ({ size: p.size, pairs: p.pairs }))
    .sort((a, b) => b.size - a.size);
  return {
    ok: true,
    mode,
    perSide: round3(
      loaded.reduce((sum, p) => round3(sum + p.size * p.pairs), 0),
    ),
    pairsLoaded: loaded,
    remainder: 0,
  };
};

/** How many plates of `size` the inventory can supply in this mode — a pair is
 *  two plates, so single-side loading can draw twice the pair count. Mirrors
 *  what `computePlates` allows itself in each loop. */
export const loadablePlateCount = (
  inventory: ReadonlyArray<PlateInventoryEntry>,
  size: number,
  mode: PlateLoadMode,
): number => {
  const pairs = inventory.find((p) => p.size === size)?.pairs ?? 0;
  return mode === "single" ? pairs * 2 : pairs;
};

/** Add (`delta` +1) or take off (−1) one plate of `size`, clamped to what the
 *  inventory holds. Entries that reach zero LEAVE the list rather than
 *  lingering at 0, so a composition never carries a plate it doesn't have. */
export const adjustComposition = (
  composition: PlateComposition,
  size: number,
  delta: number,
  maxCount: number,
): PlatePairLoaded[] => {
  const current = composition.find((p) => p.size === size)?.pairs ?? 0;
  const next = Math.max(0, Math.min(Math.floor(maxCount), current + delta));
  const rest = composition
    .filter((p) => p.size !== size)
    .map((p) => ({ size: p.size, pairs: p.pairs }));
  return (next > 0 ? [...rest, { size, pairs: next }] : rest).sort(
    (a, b) => b.size - a.size,
  );
};
